import React, { Fragment, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import HeaderTop from '../../components/HeaderTop/HeaderTop';
import Navbar from '../../components/Navbar/Navbar'
import PageTitle from '../../components/pagetitle/PageTitle'
import MapSection from '../../components/MapSection/MapSection';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Logo from '../../images/logo.svg'
import Footer from '../../components/footer/Footer';

const TrackingPage = () => {
    const [searchParams] = useSearchParams();
    const trackingId = searchParams.get('id') || '';
    const displayId = trackingId.startsWith('#') ? trackingId : `#${trackingId}`;

    // Determine the label based on which field the tracking ID matched.
    // The backend matches by: (1) unique_id, (2) order_number, (3) order_package_number.
    // Only show "Order Id:" when the input equals order_number; all other IDs are cargo-type.
    const getIdLabel = () => {
        if (!trackingData) return 'Cargo Id:';
        const { order } = trackingData;
        const tid = trackingId.replace(/^#/, '').trim();
        if (order.order_number && String(order.order_number) === tid) return 'Order Id:';
        return 'Cargo Id:';
    };

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [trackingData, setTrackingData] = useState(null);

    useEffect(() => {
        if (!trackingId) {
            setError('Please enter a tracking ID to search.');
            return;
        }

        const fetchTrackingDetails = async () => {
            setLoading(true);
            setError(null);
            try {
                // Fetch tracking details from the API
                // Route: Route::get('/order-details/tracking/{clientcode}/{order_id}', 'TrackingController@OrderTrackingDetail')
                // Any clientcode value is accepted by the backend as it is not checked in DB queries.
                const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/order-details/tracking/ofl/${encodeURIComponent(trackingId)}`);
                
                if (response.data && response.data.order) {
                    setTrackingData(response.data);
                } else {
                    setError('Tracking details not found. Please verify your tracking ID.');
                }
            } catch (err) {
                console.error(err);
                setError('Tracking details not found. Please verify your tracking ID.');
            } finally {
                setLoading(false);
            }
        };

        fetchTrackingDetails();
    }, [trackingId]);



    // Derive the real current status from task data (order.status can be stale)
    const getDisplayStatus = () => {
        if (!trackingData) return { label: 'Pending', cls: 'pending' };
        const { order, tasks } = trackingData;

        const orderStatus = (order.status || '').toLowerCase();
        if (orderStatus === 'cancelled') return { label: 'Cancelled', cls: 'failed' };

        if (tasks && tasks.length > 0) {
            const pickupTask = tasks.find(t => t.task_type_id === 1);
            const dropTask   = tasks.find(t => t.task_type_id === 2);
            const dropStatus = dropTask ? Number(dropTask.task_status) : 0;
            const pickStatus = pickupTask ? Number(pickupTask.task_status) : 0;

            if (dropStatus >= 4) return { label: 'Delivered',        cls: 'delivered' };
            if (dropStatus >= 2) return { label: 'Out for Delivery',  cls: 'pickedup'  };
            if (pickStatus >= 4) return { label: 'Picked Up',         cls: 'pickedup'  };
            
            // If tasks exist but none have reached pickup/delivery stages:
            if (pickStatus >= 1 || orderStatus === 'assigned') return { label: 'Assigned', cls: 'pending' };
            return { label: 'Pending', cls: 'pending' };
        }

        // Fallback to order.status
        if (orderStatus === 'completed') return { label: 'Delivered',       cls: 'delivered' };
        if (orderStatus === 'ongoing') return { label: 'Picked Up', cls: 'pickedup' };
        if (orderStatus === 'assigned') return { label: 'Assigned', cls: 'pending' };
        return { label: 'Pending', cls: 'pending' };
    };

    // Calculate dynamic timeline step status & dates
    const getTimeline = () => {
        if (!trackingData) return { steps: [], progressWidth: 0 };
        const { order, tasks } = trackingData;

        let pendingTime         = order.created_at || order.order_time || '';
        let pickedUpTime        = '';
        let outForDeliveryTime  = '';
        let deliveredTime       = order.completed_at || '';

        let isPendingActive        = true;
        let isPickedUpActive       = false;
        let isOutForDeliveryActive = false;
        let isDeliveredActive      = false;

        if (tasks && tasks.length > 0) {
            const pickupTask = tasks.find(t => t.task_type_id === 1);
            const dropTask   = tasks.find(t => t.task_type_id === 2);

            // task_status: 0=unassigned, 1=assigned, 2=started(out for delivery), 3=nearby, 4=completed
            if (pickupTask && Number(pickupTask.task_status) >= 4) {
                isPickedUpActive = true;
                pickedUpTime = pickupTask.Completed_time || pickupTask.completed_time || pickupTask.updated_at || '';
            }

            if (dropTask) {
                if (Number(dropTask.task_status) >= 2) {
                    isOutForDeliveryActive = true;
                    outForDeliveryTime = dropTask.started_time || dropTask.updated_at || '';
                }
                if (Number(dropTask.task_status) >= 4) {
                    isDeliveredActive = true;
                    deliveredTime = dropTask.Completed_time || dropTask.completed_time || dropTask.updated_at || '';
                }
            }
        } else {
            // Fallback when no tasks exist: use order.status
            const s = (order.status || '').toLowerCase();
            if (s === 'ongoing' || s === 'completed') isPickedUpActive = true;
            if (s === 'ongoing'  || s === 'completed') isOutForDeliveryActive = true;
            if (s === 'completed') isDeliveredActive = true;
        }

        const formatTime = (timeStr) => {
            if (!timeStr) return '';
            try {
                // Treat MySQL datetime strings as UTC by appending 'Z'
                let safeTimeStr = timeStr;
                if (typeof timeStr === 'string' && !timeStr.endsWith('Z') && timeStr.includes(' ')) {
                    safeTimeStr = timeStr.replace(' ', 'T') + 'Z';
                } else if (typeof timeStr === 'string' && !timeStr.endsWith('Z') && timeStr.includes('T')) {
                    safeTimeStr = timeStr + 'Z';
                }
                const date = new Date(safeTimeStr);
                if (isNaN(date.getTime())) return timeStr;
                
                const options = {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                };
                
                if (trackingData && trackingData.timezone) {
                    options.timeZone = trackingData.timezone;
                }
                
                return date.toLocaleString('en-US', options).replace(',', '');
            } catch (e) {
                return timeStr;
            }
        };

        const steps = [
            { label: 'Pending', active: isPendingActive, time: formatTime(pendingTime) },
            { label: 'Picked Up', active: isPickedUpActive, time: formatTime(pickedUpTime) },
            { label: 'Out for Delivery', active: isOutForDeliveryActive, time: formatTime(outForDeliveryTime) },
            { label: 'Delivered', active: isDeliveredActive, time: formatTime(deliveredTime), isLast: true }
        ];

        // Calculate horizontal progress line percentage
        let activeCount = steps.filter(s => s.active).length;
        let progressWidth = 0;
        if (activeCount > 1) {
            progressWidth = ((activeCount - 1) / (steps.length - 1)) * 100;
        }

        return { steps, progressWidth };
    };

    const { steps, progressWidth } = getTimeline();

    // Get Pickup / Destination values
    const getFooterLocations = () => {
        if (!trackingData) return { pickupFrom: 'N/A', destination: 'N/A' };
        const { order, tasks } = trackingData;

        let pickupFrom = 'N/A';
        let destination = 'N/A';

        if (tasks && tasks.length > 0) {
            const pickupTask = tasks.find(t => t.task_type_id === 1);
            const dropTask = tasks.find(t => t.task_type_id === 2);

            if (pickupTask && pickupTask.address) {
                pickupFrom = pickupTask.address;
            } else if (order.pickup_address) {
                pickupFrom = order.pickup_address;
            }

            if (dropTask && dropTask.address) {
                destination = dropTask.address;
            }
        } else {
            if (order.pickup_address) {
                pickupFrom = order.pickup_address;
            }
        }

        return { pickupFrom, destination };
    };

    const { pickupFrom, destination } = getFooterLocations();

    return (
        <Fragment>
            <HeaderTop />
            <Navbar hclass={'wpo-site-header'} Logo={Logo} />
            <section className={`wpo-breadcumb-area`}>
                <PageTitle pageTitle={'International Logistics'} pagesub={'Tracking'} />
            </section>
            <section className="track-page section-padding">
                <div className="container">
                    
                    {/* Loading Shimmer State */}
                    {loading && (
                        <div className="live-track-card skeleton-loading">
                            <div className="live-track-header">
                                <div className="title-area">
                                    <div className="skeleton-circle"></div>
                                    <div className="skeleton-bar title-bar"></div>
                                </div>
                            </div>
                            <div className="live-track-info">
                                <div className="skeleton-bar info-bar"></div>
                                <div className="skeleton-bar info-bar"></div>
                            </div>
                            <div className="live-track-timeline">
                                <div className="skeleton-timeline-line"></div>
                            </div>
                            <div className="live-track-footer">
                                <div className="skeleton-bar footer-bar"></div>
                                <div className="skeleton-bar footer-bar"></div>
                            </div>
                        </div>
                    )}

                    {/* Error / Empty Search State */}
                    {!loading && error && (
                        <div className="live-track-card error-card text-center">
                            <div className="error-icon">
                                <i className="fi flaticon-location-1"></i>
                            </div>
                            <h3>No Tracking Details</h3>
                            <p>{error}</p>
                            <form onSubmit={(e) => {
                                e.preventDefault();
                                const val = e.target.trackingInput.value.trim();
                                if (val) {
                                    window.location.search = `?id=${encodeURIComponent(val)}`;
                                }
                            }} className="error-search-form mt-4">
                                <div className="input-group">
                                    <input
                                        name="trackingInput"
                                        type="text"
                                        placeholder="Enter tracking ID or order number..."
                                        defaultValue={trackingId}
                                    />
                                    <button type="submit">Track Now</button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Loaded Tracking Details Card */}
                    {!loading && !error && trackingData && (
                        <Fragment>
                            <div className="top-search-bar">
                                <form onSubmit={(e) => {
                                    e.preventDefault();
                                    const val = e.target.trackingInput.value.trim();
                                    if (val) {
                                        window.location.search = `?id=${encodeURIComponent(val)}`;
                                    }
                                }} className="top-search-form">
                                    <div className="input-group">
                                        <input
                                            name="trackingInput"
                                            type="text"
                                            placeholder="Enter tracking ID or order number..."
                                            defaultValue={trackingId}
                                        />
                                        <button type="submit">Track Now</button>
                                    </div>
                                </form>
                            </div>
                            <div className="live-track-card">
                                <div className="live-track-header">
                                <div className="title-area">
                                    <div className="track-icon-wrapper">
                                        <i className="fi flaticon-location"></i>
                                    </div>
                                    <h2>Live Shipment Tracking</h2>
                                </div>
                                <button className="close-btn" onClick={() => window.history.back()}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                </button>
                            </div>

                            <div className="live-track-info">
                                <div className="package-info">
                                    <div style={{ marginBottom: '8px' }}>
                                        <span>{getIdLabel()}</span> <strong>{displayId}</strong>
                                    </div>
                                    {getIdLabel() === 'Cargo Id:' && trackingData && trackingData.order && trackingData.order.order_number && (
                                        <div style={{ marginBottom: '8px' }}>
                                            <span>Order Id:</span> <strong>{String(trackingData.order.order_number).startsWith('#') ? trackingData.order.order_number : `#${trackingData.order.order_number}`}</strong>
                                        </div>
                                    )}
                                    {trackingData && trackingData.order && trackingData.order.shipment_type !== 'container' && trackingData.cargo_count !== undefined && (
                                        <div>
                                            <span>Cargo Count:</span> <strong>{trackingData.cargo_count}</strong>
                                        </div>
                                    )}
                                </div>
                                <div className="status-info">
                                    <span>Shipping Status:</span> 
                                    <span className={`status-badge ${getDisplayStatus().cls}`}>
                                        {getDisplayStatus().label}
                                    </span>
                                </div>
                            </div>

                            <div className="live-track-timeline">
                                <div className="timeline-line">
                                    <div className="timeline-progress" style={{ width: `${progressWidth}%` }}></div>
                                </div>
                                <div className="timeline-steps">
                                    {steps.map((step, idx) => (
                                        <div key={idx} className={`timeline-step ${step.active ? 'active' : ''} ${step.isLast ? 'last-step' : ''}`}>
                                            {step.isLast ? (
                                                <div className="step-dot icon-dot">
                                                    <i className="fi flaticon-truck"></i>
                                                </div>
                                            ) : (
                                                <div className="step-dot"></div>
                                            )}
                                            <div className="step-label">{step.label}</div>
                                            {step.time && <div className="step-time">{step.time}</div>}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="live-track-footer">
                                <div className="footer-item">
                                    <span className="label">Pickup From:</span>
                                    <span className="value">{pickupFrom}</span>
                                </div>
                                <div className="footer-item text-right">
                                    <span className="label">Destination:</span>
                                    <span className="value">{destination}</span>
                                </div>
                            </div>
                        </div>
                        </Fragment>
                    )}
                </div>
            </section>
            <MapSection />
            {/* <CtaSection hclass={'wpo-cta-section-s2'} /> */}
            <Footer hclass={'wpo-site-footer'} />
            <Scrollbar />
        </Fragment>
    )
};

export default TrackingPage;
